import { useEffect, useState } from "react";
import { getConsent, acceptAll, rejectAll, saveConsent, CONSENT_EVENTS } from "@/lib/cookies";
import { useT } from "@/lib/i18n";
import { X, Cookie } from "lucide-react";

export const CookieConsent = () => {
  const t = useT();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const c = getConsent();
    if (!c) setVisible(true);
    else setPrefs({ analytics: !!c.analytics, marketing: !!c.marketing });

    const onOpen = () => {
      const cur = getConsent();
      if (cur) setPrefs({ analytics: !!cur.analytics, marketing: !!cur.marketing });
      setShowPrefs(true);
      setVisible(true);
    };
    window.addEventListener(CONSENT_EVENTS.OPEN, onOpen);
    return () => window.removeEventListener(CONSENT_EVENTS.OPEN, onOpen);
  }, []);

  if (!visible) return null;

  const handleAcceptAll = () => { acceptAll(); setVisible(false); setShowPrefs(false); };
  const handleRejectAll = () => { rejectAll(); setVisible(false); setShowPrefs(false); };
  const handleSavePrefs = () => { saveConsent(prefs); setVisible(false); setShowPrefs(false); };

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-4 left-4 right-4 md:bottom-6 md:left-auto md:right-6 md:w-[420px] z-[70] bg-white rounded-[6px] border border-[color:var(--gv-line)] shadow-[0_30px_80px_-20px_rgba(18,24,32,0.3)] overflow-hidden"
    >
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 text-[color:var(--gv-blue)]">
            <Cookie size={18} />
            <div className="font-display text-[17px] text-[color:var(--gv-ink)]">{t("cookies.title")}</div>
          </div>
          <button onClick={() => setVisible(false)} className="text-[color:var(--gv-ink)]/40 hover:text-[color:var(--gv-ink)]" aria-label="Close" data-testid="cookie-consent-close">
            <X size={16} />
          </button>
        </div>

        <p className="text-[13.5px] leading-relaxed text-[color:var(--gv-ink)]/75 mb-4">{t("cookies.body")}</p>

        {showPrefs && (
          <div className="space-y-3 mb-4 pt-3 border-t border-[color:var(--gv-line)]">
            <PrefRow label={t("cookies.essential")} desc={t("cookies.essential_desc")} checked disabled />
            <PrefRow label={t("cookies.analytics")} desc={t("cookies.analytics_desc")} checked={prefs.analytics} onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))} testId="cookie-pref-analytics" />
            <PrefRow label={t("cookies.marketing")} desc={t("cookies.marketing_desc")} checked={prefs.marketing} onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))} testId="cookie-pref-marketing" />
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-stretch gap-2">
          {!showPrefs ? (
            <>
              <button onClick={handleRejectAll} data-testid="cookie-reject-all" className="btn-ghost flex-1 justify-center">{t("cookies.reject_all")}</button>
              <button onClick={() => setShowPrefs(true)} data-testid="cookie-prefs" className="btn-ghost flex-1 justify-center">{t("cookies.preferences")}</button>
              <button onClick={handleAcceptAll} data-testid="cookie-accept-all" className="btn-primary flex-1 justify-center">{t("cookies.accept_all")}</button>
            </>
          ) : (
            <>
              <button onClick={handleRejectAll} className="btn-ghost flex-1 justify-center" data-testid="cookie-reject-all-2">{t("cookies.reject_all")}</button>
              <button onClick={handleSavePrefs} className="btn-primary flex-1 justify-center" data-testid="cookie-save-prefs">{t("cookies.save")}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const PrefRow = ({ label, desc, checked, onChange, disabled, testId }) => (
  <label className={`flex items-start justify-between gap-3 ${disabled ? "opacity-70" : "cursor-pointer"}`}>
    <div className="flex-1 min-w-0">
      <div className="text-[13.5px] font-medium text-[color:var(--gv-ink)]">{label}</div>
      <div className="text-[12px] text-[color:var(--gv-ink)]/55">{desc}</div>
    </div>
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange && onChange(!checked)}
      data-testid={testId}
      className={`shrink-0 w-10 h-6 rounded-full border transition-all relative ${checked ? "bg-[color:var(--gv-pink)] border-[color:var(--gv-pink)]" : "bg-[color:var(--gv-mist)] border-[color:var(--gv-line)]"}`}
      aria-pressed={checked}
    >
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : ""}`} />
    </button>
  </label>
);

export default CookieConsent;
